import React, { type HTMLAttributes } from 'react';
import { getScanStatusColor } from 'src/utils';
import styled, { DefaultTheme } from 'styled-components';

export const DOCUMENT_EDITOR_LAYOUT_CLASS = 'document-editor-layout';

export const DragDropOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  background: rgba(0, 0, 0, 0.01);
  cursor: pointer;
`;

export const EditorTitleContainer = styled.div`
  flex-shrink: 0;
  height: 80px;
  background-color: white;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px 8px 0 0;
  box-sizing: border-box;
`;

export const EditorContainer = styled.div`
  background-color: white;
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 16px;
  width: 100%;
  flex-grow: 2;

  @media screen and (max-width: 1400px) {
    display: flex;
    flex-direction: column;
  }

  > div {
    flex-grow: 2;
    border: none;
  }
`;

export const FileUploadContainer = styled.div`
  height: calc(100% - 21px);
  flex-grow: 2;
  border-left: 1px solid grey;

  @media screen and (max-width: 1400px) {
    border-top: 1px solid grey;
    border-left: none;
  }

  > .filepond--wrapper {
    margin: 0;
    padding: 10px;
  }
  .filepond--drop-label {
    position: relative;
    width: 100%;
    align-items: center;
    background-color: white;
  }
  .filepond--root {
    margin-top: 5px;
    margin-right: 1px;
  }
`;

export const BarExtension = styled.div`
  height: 51px;
  background-color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N200
      : props.theme.atlas.semantic.color.surface.variant};
  border-bottom: 1px solid
    ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N300
        : props.theme.atlas.semantic.color.outline.fixed};
`;

export const DocumentEditorContainer = styled.div<
  { $disableLegacyStyles?: boolean } & HTMLAttributes<HTMLDivElement>
>`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
  box-sizing: border-box;
  ${(props) =>
    props.$disableLegacyStyles
      ? `
  width: 100%;
  `
      : `
  padding: 30px;
  width: 100%;
  `}
`;

export const UploadsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px;

  .filepond--wrapper {
    margin: 10px 0;
    border-radius: 5px;
    border: 1px solid
      ${(props) =>
        props.theme.isVaultVariant
          ? props.theme.newPalette.neutral.N300
          : props.theme.atlas.semantic.color.outline.fixed};
  }
`;

export const UploadsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const getUploadedFileStyle = (t: DefaultTheme): React.CSSProperties => ({
  background: 'none',
  border: `1px solid ${t.isVaultVariant ? t.newPalette.turquoise.T300 : t.atlas.semantic.color.action.primary.default}`,
  borderRadius: '5px',
  width: 'auto',
  justifyContent: 'space-between',
  textTransform: 'none',
  padding: '0',
});

export const UploadedFileText = styled.div`
  font-size: 12px;
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
  padding: 19px 14px 19px 14px;
  width: 100%;
  text-align: left;
`;

export const UploadedFileName = styled.div<{ $status?: string }>`
  font-size: 12px;
  color: ${(props) => getScanStatusColor(props.$status, props.theme)};
  font-weight: 500;
  cursor: ${(props) =>
    props.$status === 'CLEAN' || props.$status === 'MANUALLY_APPROVED'
      ? 'pointer'
      : 'not-allowed'};
  opacity: ${(props) =>
    props.$status === 'CLEAN' || props.$status === 'MANUALLY_APPROVED'
      ? '1'
      : '0.7'};
  padding: 19px 14px 19px 14px;
  width: 100%;
  text-align: left;
`;

export const RemoveFileIcon = styled.img`
  margin: 7px 14px 7px 7px;
`;

export const EDITOR_STYLES = {
  border: '1.5px solid #fff',
  borderRadius: '8px',
  background: '#fff',
  padding: '12px',
  marginBottom: '16px',
} as const;

export const UPLOAD_OVERLAY_STYLES = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: 2,
  cursor: 'pointer',
  background: 'transparent',
  pointerEvents: 'auto' as const,
};

export const SPINNER_OVERLAY_STYLES = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 3,
  background: 'rgba(255,255,255,0.5)',
  borderRadius: '8px',
};

export const CANCEL_ICON_STYLES = {
  position: 'absolute' as const,
  top: 8,
  right: 8,
  cursor: 'pointer',
  width: 24,
  height: 24,
  opacity: 0.8,
};
